import React, { FC } from 'react';
import { renderRoutes, RouteConfig } from 'react-router-config';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { StyleSheetManager } from 'styled-components';

import useDeviceContext from '@rover/react-lib/src/context/useDeviceContext';
import usePageContext from '@rover/react-lib/src/context/usePageContext';
import { handleShouldForwardProp } from '@rover/rsdk/src/';
import { useObservabilityMetadataConceptInfo } from '@rover/rsdk/src/index.common';
import httpContext from '@rover/rsdk/src/modules/Network/httpContext';
import useLifecycleEventsShim from '@rover/shared/js/eventstream/useLifecycleEventsShim';
import usePerfTrackingEventsShim from '@rover/shared/js/eventstream/usePerfTrackingEventsShim';
import useCurrentUserShim from '@rover/shared/js/hooks/useCurrentUserShim';
import Footer from '@rover/shared/js/ssr/BaseTemplate/Footer';
import Header from '@rover/shared/js/ssr/BaseTemplate/Header';
import Sentry from '@rover/shared/js/ssr/BaseTemplate/Sentry';
import getFullUrl from '@rover/shared/js/utils/getFullUrl';

import GlobalModals from './GlobalModals';
import HtmlHeader from './HtmlHeader';
import useObservabilityMetadataHandler from './useObservabilityMetadataHandler';
import usePageLoadHandler from './usePageLoadHandler';

type Props = RouteComponentProps & {
  baseRoute: RouteConfig;
};

const BaseTemplate: FC<Props> = (props) => {
  const { baseRoute, history } = props;
  const { pageName: uiName, concept, componentName: uiComponentName } = baseRoute || {};

  const { requiresAuth } = usePageContext();
  const { isMobile, isEmbedded } = useDeviceContext();
  const { isAuthenticated, user } = useCurrentUserShim();

  useLifecycleEventsShim(history);
  usePerfTrackingEventsShim();
  useObservabilityMetadataHandler(); // needs to go before usePageLoadHandler() so locale is set
  usePageLoadHandler();
  useObservabilityMetadataConceptInfo({ concept, uiComponentName, uiName });

  if (process.env.JS_ENV_SERVER && requiresAuth && !isAuthenticated) {
    const res = httpContext.getRes();
    // don't redirect if we've already sent a response
    if (!res?.headersSent) {
      res?.redirect(302, `/account/continue/?next=${encodeURIComponent(getFullUrl())}`);
    }
    return null;
  }

  return (
    <StyleSheetManager shouldForwardProp={handleShouldForwardProp}>
      <Sentry userOpk={user?.opk}>
        <HtmlHeader />
        <Header />
        <main role="main" id="base-content">
          {renderRoutes(baseRoute.routes, { isMobile, isEmbedded })}
        </main>
        <GlobalModals />
        <Footer />
      </Sentry>
      <ReactQueryDevtools />
    </StyleSheetManager>
  );
};

export default withRouter(BaseTemplate);
